import { useLayoutEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { useReadiness } from '@/lib/readiness/useReadiness'
import { IndexSelector } from '@/components/readiness/IndexSelector'
import { readinessCrumbs, readinessPageTitle } from '@/lib/readiness/util'
import { useCatalogueStore } from '@/stores/catalogueStore'
import { useOrigin } from '@/lib/readiness/nav'
import { Breadcrumb } from '@/components/layout/Breadcrumb'

// Rendered by the platform Layout on /readiness routes (§4.2): the top-bar
// breadcrumb next to the Quantizant wordmark, and the index selector in the
// slot where Risk shows its view selector. The breadcrumb follows the page's
// origin (I5 §4): `Readiness › Dashboard › Progress › TE-2` when TE-2 was
// opened from Progress.

export function ReadinessBreadcrumb() {
  const { indexId, scope } = useReadiness()
  const { pathname } = useLocation()
  const origin = useOrigin()
  const techs = useCatalogueStore((s) => s.technologies)
  const crumbs = readinessCrumbs(pathname, {
    indexId,
    trail: origin.trail,
    techName: (id) => techs.find((t) => t.id === id)?.name ?? null,
    snapshotName: (id) => scope.snapshots.find((s) => s.id === id)?.name ?? null,
  })
  // The module name leads so the crumb mirrors the sidebar (Readiness › Dashboard › …).
  return <Breadcrumb crumbs={[{ label: 'Readiness', to: `/readiness?index=${indexId}` }, ...crumbs]} testId="readiness-breadcrumb" />
}

export function ReadinessIndexSelector() {
  const { index, indexes, selectIndex } = useReadiness()
  return <IndexSelector indexes={indexes} current={index} onSelect={selectIndex} />
}

// Owns `document.title` while a Readiness route is shown (I4 §11): set on
// every navigation (strip tab, back / forward, deep link) before paint, and the
// platform's own title restored when the module is left.
export function ReadinessDocumentTitle() {
  const { pathname } = useLocation()
  const techs = useCatalogueStore((s) => s.technologies)
  const previous = useRef<string | null>(null)
  useLayoutEffect(() => {
    previous.current = document.title
    return () => {
      if (previous.current != null) document.title = previous.current
    }
  }, [])
  useLayoutEffect(() => {
    document.title = readinessPageTitle(pathname, (id) => techs.find((t) => t.id === id)?.name ?? null)
  }, [pathname, techs])
  return null
}
